import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  size?: 'default' | 'wide';
  children: React.ReactNode;
  footer?: React.ReactNode;
};
const sizeClasses = {
  default: 'max-w-[640px]',
  wide: 'max-w-[960px]'
} as const;
export function Modal({
  open,
  onClose,
  title,
  subtitle,
  size = 'default',
  children,
  footer
}: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  return <AnimatePresence data-id="element-401" data-mp="9ybma3k8rbj7">
      {open && <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:items-center sm:p-6" data-id="element-402" data-mp="9zmrm7tc4rcf">
          <motion.button type="button" aria-label="Close dialog" onClick={onClose} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15
      }} className="fixed inset-0 h-full w-full cursor-default bg-ink/40" data-id="element-403" data-mp="e06n76y9fmwg" />
          <motion.div role="dialog" aria-modal="true" aria-label={title} initial={{
        opacity: 0,
        y: 12,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 12,
        scale: 0.98
      }} transition={{
        type: 'spring',
        stiffness: 420,
        damping: 34
      }} className={['relative flex max-h-[88vh] w-full flex-col overflow-hidden rounded-xl bg-white shadow-2xl', sizeClasses[size]].join(' ')} data-id="element-404" data-mp="2hddhoc43eikk">
            <header className="flex items-start gap-3 border-b border-gray-200 px-5 py-4" data-id="element-405" data-mp="1niuw5ywhsei">
              <div className="min-w-0" data-id="element-406" data-mp="3sysji9d018yh">
                <h2 className="truncate text-[16px] font-bold text-ink" data-id="element-407" data-mp="1r21lsqw9k9dc">{title}</h2>
                {subtitle && <p className="mt-0.5 truncate text-[12.5px] text-gray-500" data-id="element-408" data-mp="1w1xtp1830ky6">{subtitle}</p>}
              </div>
              <button type="button" onClick={onClose} aria-label="Close" className="ml-auto rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100" data-id="element-409" data-mp="1hc8kqjtqzcnc">
                <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-410" data-mp="1cdowgcrolort" />
              </button>
            </header>
            <div className="scrollbar-thin flex-1 overflow-y-auto" data-id="element-411" data-mp="ye5szx9tf1lu">{children}</div>
            {footer && <div className="border-t border-gray-200 bg-gray-50 px-5 py-3" data-id="element-412" data-mp="13ch2g6w77a3j">{footer}</div>}
          </motion.div>
        </div>}
    </AnimatePresence>;
}